import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { useParams } from 'react-router';

import { Checkbox, Button, Modal } from '@clearcover/atlas';
import {
  getMailableDocumentsResponseType,
  PolicyDocumentType,
} from '@clearcover/core-app-types';

import { Asset, Table, useToast } from 'Shared/components';
import { useModalHashChange } from 'Shared/hooks';
import { useLazyApi, useNewApi } from 'Shared/services/Api';
import { formatDateTimeFullUsingTimezone, sortByDate } from 'Shared/utils';

import { MultilineAddress } from 'Policies/components/Address';
import { usePolicyContext } from 'Policies/utils/PolicyContext/PolicyContext';

import styles from './style.module.css';

export const MailDocuments: FC = () => {
  const { id: policyId } = useParams<{ id: string }>();
  const {
    details: { timeZone },
  } = usePolicyContext();
  const { modalHashChange } = useModalHashChange('mailDocuments');

  const [selectedDocs, setCheckedDocs] = useState<Set<string>>(new Set());
  const [documentsMailed, setDocumentsMailed] = useState<boolean>(false);
  const [effectiveDateSortDirection, setSortDirection] = useState<
    'desc' | 'asc' | 'none'
  >('desc');

  const { toast } = useToast();

  const toggleDocument = (document: PolicyDocumentType) => {
    setCheckedDocs(checked => {
      checked.has(document.id)
        ? checked.delete(document.id)
        : checked.add(document.id);
      return new Set(checked);
    });
  };

  const [{ data, loading: loadingMailableDocuments }] = useNewApi<
    getMailableDocumentsResponseType
  >({ url: `/policies/${policyId}/mailableDocuments` }, true, false);

  const [{ loading: mailingDocuments }, mailDocuments] = useLazyApi<void>({
    url: `policies/${policyId}/mailDocuments`,
    method: 'POST',
  });
  const { mailingAddress, documents } = data || {
    mailingAddress: {},
    documents: [],
  };

  return (
    <Modal
      onVisibilityChange={modalHashChange}
      variant="normal"
      toggle={openModal =>
        documents.length > 0 ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={openModal}
            data-testid="open-mail-documents"
          >
            <span>Mail Documents</span>
          </Button>
        ) : (
          <></>
        )
      }
    >
      {close => (
        <div className={styles.container}>
          <div className={styles.title}>
            <h3>Mail Documents</h3>
          </div>
          <div className={styles.subTitle}>
            <h5>Select Documents</h5>
          </div>
          <div className={styles.tableContainer}>
            <Table
              className={styles.table}
              striped={false}
              loading={loadingMailableDocuments}
              headers={[
                '',
                'Description',
                {
                  title: 'Effective Date',
                  onClick: () => {
                    setSortDirection(direction =>
                      direction === 'asc' ? 'desc' : 'asc'
                    );
                  },
                  sortDirection: effectiveDateSortDirection,
                },
              ]}
              name={'Mail Documents'}
              columnWidths={['40px', 3, '200px']}
              rows={documents
                .sort(sortByDate(effectiveDateSortDirection, 'createdAt'))
                .map(document => ({
                  key: document.id,
                  className: classNames(styles.documentItem, {
                    [styles.checked]: selectedDocs.has(document.id),
                  }),
                  onClick: () => toggleDocument(document),
                  data: [
                    {
                      allowOverflow: true,
                      component: (
                        <Checkbox
                          onChange={() => {
                            // intentionally blank; row click handles toggle
                          }}
                          readOnly
                          value={selectedDocs.has(document.id)}
                          label=""
                          name={`document-${document.id}`}
                          className={styles.checkbox}
                        />
                      ),
                    },
                    <div className={styles.titleAndAsset}>
                      <Asset variant={'pdf'} />
                      <span className={styles.documentName}>
                        {document.name}
                      </span>
                    </div>,
                    <span className={styles.date}>
                      {' '}
                      {formatDateTimeFullUsingTimezone(
                        document.createdAt,
                        timeZone
                      )}
                    </span>,
                  ],
                }))}
            />
          </div>
          <div className={styles.mailingAddress}>
            <h4>Mailing Address</h4>
            <MultilineAddress
              testId={'documents-mailing-address'}
              address={mailingAddress}
            />
          </div>

          <div className={styles.buttons}>
            <Button
              disabled={mailingDocuments}
              onClick={close}
              size={'md'}
              variant={'secondary'}
            >
              Cancel
            </Button>
            <Button
              data-testid={`mailDocuments-button`}
              disabled={selectedDocs.size < 1 || documentsMailed}
              isSubmitting={mailingDocuments}
              onClick={() => {
                mailDocuments({
                  data: { documentIds: Array.from(selectedDocs) },
                })
                  .then(() => {
                    toast({
                      message: `${
                        selectedDocs.size === 1 ? 'Document' : 'Documents'
                      } mailed`,
                      type: 'success',
                      position: 'bottom',
                      withSecondaryNav: false,
                    });
                    setTimeout(close, 900);
                    setDocumentsMailed(true);
                  })
                  .catch(() => {
                    toast({
                      message: 'Cannot send documents',
                      type: 'error',
                      position: 'bottom',
                      withSecondaryNav: false,
                    });
                  });
              }}
              size={'md'}
              variant="primary"
            >
              Mail {selectedDocs.size}{' '}
              {selectedDocs.size === 1 ? 'Document' : 'Documents'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
